import {
  Outlet,
  createRootRoute,
  HeadContent,
  Scripts,
} from '@tanstack/react-router'
import type { ReactNode } from 'react'
import HubloProvider from '../components/system/HubloProvider'
import {
  appAbsoluteUrlOrigin,
  appBrandConfig,
} from '../branding/appBrandConfig'
import appCss from '../styles.css?url'
import { reactRefreshPreamble } from '../utils/dev-preamble'
import { getBrandShellConfig } from '@front/theme'

const brandTokenCssHref = `/branding/${appBrandConfig.brandId}/styles/theme-color-tokens.${appBrandConfig.brandId}.css`
const brandShellConfig = getBrandShellConfig(appBrandConfig, {
  absoluteUrlOrigin: appAbsoluteUrlOrigin,
})
const fontLinks = brandShellConfig.links.filter((link) =>
  link.scope === undefined
    ? link.rel === 'preconnect' || link.rel === 'stylesheet'
    : link.scope === 'fonts',
)
const fontStyles = brandShellConfig.styles

export const Route = createRootRoute({
  ssr: true,
  shellComponent: RootDocument,
  headers: () => ({ 'cache-control': 'no-store' }),
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: 'Hublo Career' },
    ],
    links: [
      {
        rel: 'icon',
        href: `/branding/${appBrandConfig.brandId}/icons/favicon.ico`,
        type: 'image/x-icon',
      },
      { rel: 'stylesheet', href: brandTokenCssHref },
      { rel: 'stylesheet', href: appCss },
    ],
  }),
  component: RootComponent,
})

function RootComponent() {
  return (
    <HubloProvider>
      <Outlet />
    </HubloProvider>
  )
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html>
      <head>
        {/* Establish @layer order before Emotion or any other CSS runs */}
        <style>@layer theme, base, mui, components, utilities;</style>
        <HeadContent />
        {fontLinks.map((link) => (
          <link
            key={link.key}
            data-branding-key={link.key}
            data-branding-scope="fonts"
            rel={link.rel}
            href={link.href}
            as={link.as}
            type={link.type}
            {...(link.crossOrigin !== undefined
              ? { crossOrigin: link.crossOrigin }
              : {})}
          />
        ))}
        {fontStyles.map((style) => (
          <style
            key={style.key}
            data-branding-key={style.key}
            data-branding-scope="fonts"
            dangerouslySetInnerHTML={{ __html: style.cssText }}
          />
        ))}
        {import.meta.env.DEV && (
          <script
            type="module"
            suppressHydrationWarning
            dangerouslySetInnerHTML={{ __html: reactRefreshPreamble }}
          />
        )}
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  )
}
